import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight, Refresh } from 'iconsax-reactjs';
import { type FC, useCallback, useEffect, useRef } from 'react';
import type { WealthConvert } from '@/backend';
import { Button } from '@/shared/Button';
import { Coin } from '@/shared/Coin';
import { Dialog } from '@/shared/Dialog';
import { Digits } from '@/shared/Digits';
import { useCountdown, useIsDesktop } from '@/shared/react';
import { useConvertVerify } from './api';

export const ConvertConfirmationModal: FC<{
  data?: WealthConvert;
  onCancel?: () => void;
  onRefresh?: () => void;
  onFinish?: () => void;
  type: 'buy' | 'sell';
}> = ({ onCancel, onRefresh, data, type, onFinish }) => {
  const { t } = useLingui();
  const isDesktop = useIsDesktop();
  const cn = useCountdown();
  const isStarted = useRef(false);
  const verify = useConvertVerify();

  const handleFinish = useCallback(() => {
    verify.reset();
    onFinish?.();
  }, [verify, onFinish]);

  useEffect(() => {
    if (!data) {
      cn.stop();
      isStarted.current = false;
    } else if (!isStarted.current) {
      cn.start(30_000);
      isStarted.current = true;
    }
  }, [data, cn.start, cn.stop]);

  useEffect(() => {
    if (verify.data) {
      handleFinish();
    }
  }, [verify.data, handleFinish]);

  return (
    <>
      <Dialog
        open={!!data}
        onClose={onCancel}
        position={isDesktop ? 'center' : 'bottom'}
        className={isDesktop ? '' : ''}
        header={t`Confirm`}
      >
        <div className="flex items-center justify-between gap-2 px-widget relative">
          <div className="flex flex-col gap-1 items-center justify-center">
            <Coin size={10} coin={data?.from_coin} variant="icon" />
            <p className="text-content-tertiary text-xs">{t`You Pay`}</p>
            <Digits value={data?.from_amount} coin={data?.from_coin} />
          </div>
          <ArrowRight
            className="auto-rtl absolute left-1/2 -translate-x-1/2"
            size={24}
          />
          <div className="flex flex-col gap-1 items-center justify-center">
            <Coin size={10} coin={data?.to_coin} variant="icon" />
            <p className="text-content-tertiary text-xs">{t`You Receive`}</p>
            <Digits value={data?.to_amount} coin={data?.to_coin} />
          </div>
        </div>
        <div className="text-content-tertiary text-sm text-center p-4 h-12">
          {cn.value > 0 && (
            <Trans>
              Price Valid Till <span>{cn.value}</span> Sec
            </Trans>
          )}
        </div>
        {cn.value > 0 ? (
          <Button
            size={12}
            type="submit"
            variant={type === 'buy' ? 'brand' : 'negative'}
            loading={verify.isMutating}
            disabled={!data}
            block
            className="w-full"
            autoFocus
            onClick={() =>
              data?.tracking_code &&
              verify.trigger({ slug: data?.tracking_code })
            }
          >
            {type === 'buy' ? t`Confirm Buying` : t`Confirm Selling`}
            <Coin
              size={6}
              coin={type === 'buy' ? data?.to_coin : data?.from_coin}
              variant="symbol"
            />
          </Button>
        ) : (
          <Button
            size={12}
            block
            className="w-full"
            type="button"
            variant="outline"
            onClick={onRefresh}
            autoFocus
          >
            <Refresh size={16} className="me-2" />
            {t`Refresh Price`}
          </Button>
        )}
      </Dialog>
      {/* <Dialog
        position={isDesktop ? 'center' : 'bottom'}
        open={!!verify.data}
        onClose={handleFinish}
        header={t`Trade Submitted`}
      >
        <div className="bg-background-disabled rounded-xl p-3 flex flex-col gap-3 text-sm">
          <div className="flex items-center gap-1 justify-between">
            <p className="text-content-tertiary">{t`Paid`}</p>
            <Digits
              value={verify.data?.from_amount}
              coin={verify.data?.from_coin}
            />
          </div>
          <div className="flex items-center gap-1 justify-between">
            <p className="text-content-tertiary">{t`Received`}</p>
            <Digits
              value={verify.data?.to_amount}
              coin={verify.data?.to_coin}
            />
          </div>
          <div className="flex items-center gap-1 justify-between">
            <p className="text-content-tertiary">{t`Price`}</p>
            <Digits
              value={verify.data?.price}
              coin={
                verify.data?.is_sell
                  ? verify.data?.to_coin
                  : verify.data?.from_coin
              }
            />
          </div>
          <div className="flex items-center gap-1 justify-between">
            <p className="text-content-tertiary">{t`Tracking Code`}</p>
            <p>{verify.data?.tracking_code}</p>
          </div>
          <div className="flex items-center gap-1 justify-between">
            <p className="text-content-tertiary">{t`Date`}</p>
            <DateTime
              value={
                verify.data?.created_at ? verify.data?.created_at * 1000 : 0
              }
              variant="date_time"
            />
          </div>
        </div>
        <div className="flex gap-3 items-center mt-widget">
          <Link
            to={`/transactions?type=convert&highlight=${verify.data?.tracking_code}`}
            className="grow basis-1"
          >
            <Button
              variant="outline"
              size={12}
              block
              type="div"
            >{t`View History`}</Button>
          </Link>
          <Button
            variant="brand"
            size={12}
            block
            type="button"
            className="grow basis-1"
            onClick={handleFinish}
          >{t`OK`}</Button>
        </div>
      </Dialog> */}
    </>
  );
};
